/**
 * useVideosList Hook
 *
 * Hook customizado para gerenciar lista de vídeos com:
 * - Paginação
 * - Filtros (busca, status, ordenação)
 * - Cache automático via React Query
 * - Loading states
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { videoService, VideosListParams, Video } from '@/services/videoService';
import { useState, useCallback } from 'react';

interface UseVideosListOptions {
  initialPage?: number;
  initialLimit?: number;
  initialSearch?: string;
  initialStatus?: 'processing' | 'active' | 'error';
  initialSortBy?: 'createdAt' | 'title' | 'plays';
  initialSortOrder?: 'ASC' | 'DESC';
  enabled?: boolean; // Permite controlar quando a query é executada
}

export function useVideosList(options: UseVideosListOptions = {}) {
  const queryClient = useQueryClient();

  // Estados dos filtros
  const [page, setPage] = useState(options.initialPage || 1);
  const [limit] = useState(options.initialLimit || 50);
  const [search, setSearch] = useState(options.initialSearch || '');
  const [status, setStatus] = useState<'processing' | 'active' | 'error' | undefined>(
    options.initialStatus
  );
  const [sortBy, setSortBy] = useState<'createdAt' | 'title' | 'plays'>(
    options.initialSortBy || 'createdAt'
  );
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(
    options.initialSortOrder || 'DESC'
  );

  // Construir parâmetros da query
  const queryParams: VideosListParams = {
    page,
    limit,
    search: search || undefined,
    status,
    sortBy,
    sortOrder,
    // Buscar significa "achar em qualquer lugar": durante a busca incluímos os
    // vídeos que estão dentro de pastas, que ficam de fora da navegação normal.
    // Sem busca o parâmetro não vai, para a raiz continuar separada das pastas.
    includeFolderVideos: search ? true : undefined,
  };

  // Query key para cache
  const queryKey = ['videos', 'list', queryParams];

  // Buscar vídeos
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => videoService.getVideosList(queryParams),
    enabled: options.enabled !== false,
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5, // 5 minutos (antes era cacheTime)
  });

  // Métodos de navegação
  const goToNextPage = useCallback(() => {
    if (data?.pagination.hasNextPage) {
      setPage(prev => prev + 1);
    }
  }, [data?.pagination.hasNextPage]);

  const goToPrevPage = useCallback(() => {
    if (data?.pagination.hasPrevPage) {
      setPage(prev => prev - 1);
    }
  }, [data?.pagination.hasPrevPage]);

  const goToPage = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= (data?.pagination.totalPages || 1)) {
      setPage(newPage);
    }
  }, [data?.pagination.totalPages]);

  // Métodos de filtro
  const updateSearch = useCallback((newSearch: string) => {
    setSearch(newSearch);
    setPage(1); // Reset para primeira página ao buscar
  }, []);

  const updateStatus = useCallback((newStatus: 'processing' | 'active' | 'error' | undefined) => {
    setStatus(newStatus);
    setPage(1);
  }, []);

  const updateSort = useCallback((
    newSortBy: 'createdAt' | 'title' | 'plays',
    newSortOrder?: 'ASC' | 'DESC'
  ) => {
    setSortBy(newSortBy);
    if (newSortOrder) {
      setSortOrder(newSortOrder);
    } else {
      // Toggle sort order se já estiver ordenando por este campo
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    }
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setSearch('');
    setStatus(undefined);
    setSortBy('createdAt');
    setSortOrder('DESC');
    setPage(1);
  }, []);

  // Invalidar cache (útil após deletar/atualizar vídeo)
  const invalidateCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['videos', 'list'] });
  }, [queryClient]);

  // Prefetch da próxima página para melhor UX
  const prefetchNextPage = useCallback(() => {
    if (data?.pagination.hasNextPage) {
      const nextParams = { ...queryParams, page: page + 1 };
      queryClient.prefetchQuery({
        queryKey: ['videos', 'list', nextParams],
        queryFn: () => videoService.getVideosList(nextParams),
      });
    }
  }, [data?.pagination.hasNextPage, page, queryParams, queryClient]);

  return {
    // Dados
    videos: data?.videos || [],
    pagination: data?.pagination,

    // Estados
    isLoading,
    isFetching,
    isError,
    error,

    // Filtros atuais
    filters: {
      page,
      limit,
      search,
      status,
      sortBy,
      sortOrder,
    },

    // Métodos de navegação
    goToNextPage,
    goToPrevPage,
    goToPage,

    // Métodos de filtro
    updateSearch,
    updateStatus,
    updateSort,
    clearFilters,

    // Utilitários
    refetch,
    invalidateCache,
    prefetchNextPage,
  };
}

export default useVideosList;
